import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, useChatContext } from 'stream-chat-react'
import api from 'services/api'
import { isDate, toDateString } from 'services/utils'
import CopyToClipboard from 'components/core/CopyToClipboard'
import PhoneIcon from '@mui/icons-material/PhoneOutlined'
import EmailIcon from '@mui/icons-material/EmailOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

const EXCLUDED_METRICS = ['username', 'age', 'signup_date']

function renderMetric(value) {
  const dateRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
  return typeof value === 'string' && value.match(dateRegex)
    ? toDateString(value)
    : value
}

function getAge(birthdayStr) {
  const birthday = new Date(birthdayStr)
  if (!isDate(birthday)) return null
  const ageDifMs = Date.now() - birthday
  const ageDate = new Date(ageDifMs)
  return Math.abs(ageDate.getUTCFullYear() - 1970)
}

const Heading = ({ title }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontStyle: 'italic',
      color: 'text.secondary',
      mx: 2,
      fontSize: '0.9em',
    }}
  >
    <Divider sx={{ flex: 1 }} />
    <Box sx={{ mx: 1 }}>{title}</Box>
    <Divider sx={{ flex: 1 }} />
  </Box>
)

// The thread's user's phone and email, whichever they have, from the admin
// API by user id (Stream's user carries neither). A read that lands after the
// thread changed is dropped.
const useContact = (userId) => {
  const [contact, setContact] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    setContact(null)
    setError(null)
    if (!userId) return undefined
    let live = true
    api.users
      .getUserContact(userId)
      .then((data) => live && setContact(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [userId])
  return { contact, error }
}

const OBJECT_ID = /^[0-9a-f]{24}$/i

// Whether the thread's user has a profile on the Users page: the list holds
// everyone who has streamed (a span row, 90 days) and, on production, no
// other staff. Asked of the list narrowed to them, one row or none. A thread
// with a bot has no account id to ask about, and a failed read shows no link.
const useUsersPageProfile = (userId) => {
  const [listed, setListed] = useState(false)
  useEffect(() => {
    setListed(false)
    if (!userId || !OBJECT_ID.test(userId)) return undefined
    let live = true
    api.streamMetrics
      .getUsers({ userId, limit: 1 })
      .then((data) => live && setListed(data.users.length > 0))
      .catch(() => live && setListed(false))
    return () => {
      live = false
    }
  }, [userId])
  return listed
}

const ContactRow = ({ Icon, value, title }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      wordBreak: 'break-all',
    }}
  >
    <Icon sx={{ fontSize: '1.1em', color: 'text.secondary' }} />
    <Box>{value}</Box>
    <CopyToClipboard data={value} title={title} />
  </Box>
)

// `actions` go under the contact details: a phone's thread actions.
const UserProfile = ({ actions }) => {
  const { channel: activeChannel, client } = useChatContext()
  const [metrics, setMetrics] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const user = useMemo(() => {
    if (!activeChannel) return null
    const { members } = activeChannel.state
    const userId = Object.keys(members).filter((id) => id !== client.userID)[0]
    return members[userId]?.user
  }, [activeChannel, client.userID])

  const loadMetrics = useCallback(async (user) => {
    if (!user) return setMetrics(null)

    setIsLoading(true)
    try {
      const newMetrics = await api.networks.getUserMetrics(user.username)
      setMetrics(newMetrics)
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMetrics(user)
  }, [user, loadMetrics])

  const { contact, error: contactError } = useContact(user?.id)
  const onUsersPage = useUsersPageProfile(user?.id)

  if (!user) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 2,
          py: 3,
        }}
      >
        <Avatar name={user.name} image={user.image} size={80} />
        <Box sx={{ fontWeight: 700, mt: 2 }}>{user.name}</Box>
        <Box sx={{ mt: 1, fontSize: '0.9em', textAlign: 'center' }}>
          <Box>@{user.username}</Box>
          <Box>{getAge(user.date_of_birth)} years old</Box>
          <Box>Joined {toDateString(user.signup_date)}</Box>
          <Box sx={{ mt: 1 }}>
            {contact?.phone_number && (
              <ContactRow
                Icon={PhoneIcon}
                value={contact.phone_number}
                title="Copy phone number"
              />
            )}
            {contact?.email && (
              <ContactRow
                Icon={EmailIcon}
                value={contact.email}
                title="Copy email"
              />
            )}
            {contactError && (
              <Box sx={{ fontStyle: 'italic' }}>
                Contact unavailable: {contactError}
              </Box>
            )}
          </Box>
          {/* A new tab, so the thread keeps its place. */}
          {onUsersPage && (
            <Button
              size="small"
              component={Link}
              to={`/users?user=${user.id}`}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              User profile
            </Button>
          )}
        </Box>
        {actions}
      </Box>
      <Heading title="metrics" />
      <Box sx={{ textAlign: 'center', py: 2, flex: 1 }}>
        {(() => {
          if (isLoading) return null

          if (error)
            return <Box sx={{ fontStyle: 'italic' }}>Error: {error}</Box>

          if (!metrics)
            return <Box sx={{ fontStyle: 'italic' }}>No metrics available</Box>

          return (
            <Box sx={{ px: 4, py: 1 }}>
              {Object.keys(metrics)
                .filter((field) => !EXCLUDED_METRICS.includes(field))
                .map((field) => (
                  <Box
                    key={field}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8em',
                    }}
                  >
                    <Box sx={{ fontWeight: 700, mr: 1 }}>{field}:</Box>
                    <Box>{renderMetric(metrics[field])}</Box>
                  </Box>
                ))}
            </Box>
          )
        })()}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          justifyContent: 'center',
          fontSize: '0.8em',
        }}
      >
        <Box
          sx={{
            fontStyle: 'italic',
            color: 'text.secondary',
            textAlign: 'center',
            mr: 1,
          }}
        >
          {user.id}
        </Box>
        <CopyToClipboard data={user.id} title="Copy user id" />
      </Box>
    </Box>
  )
}

export default UserProfile
